import { useQuery } from '@tanstack/react-query'
import { onLine, PrintRows, PrintRunning, type Rec, resourceApi, SignatureMark, useCompany, useSignatures } from '@tnn/app-kit'
import { bahtText, formatArea, formatDate, formatMoney, formatNumber, formatPhone, formatTaxId, type RefMap } from '@tnn/core'
import { type InvoiceLine, invoiceTotals } from '@tnn/domain'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import classes from './document.module.css'

/** The customer's tax invoice address: house number and road, then subdistrict, district, province and postcode */
export function CustomerAddress({ customer }: { customer?: Rec }) {
  if (!customer) return null
  const area = formatArea({
    subdistrict: customer.subdistrict as string | null,
    district: customer.district as string | null,
    province: customer.province as string | null,
    postcode: customer.postcode as string | null,
  })
  return <div>{[customer.address, area].filter(Boolean).join(' ')}</div>
}

/**
 * The customer's tax identity under their name on a printed document: a company's tax ID and
 * branch (สำนักงานใหญ่ / สาขา); an individual's national ID, with no branch.
 */
export function CustomerTaxLine({ customer }: { customer?: Rec }) {
  const { t } = useTranslation()
  if (!customer?.taxId) return null
  const id = formatTaxId(String(customer.taxId))
  if (customer.partyType === 'individual') return <div>{`${t('party.idCard')} ${id}`}</div>
  const b = customer.branch as string | null | undefined
  const branch = !b || b === '00000' ? t('accounting:customer.headOffice') : `${t('accounting:customer.fields.branch')} ${b}`
  return <div>{`${t('accounting:customer.fields.taxId')} ${id} · ${branch}`}</div>
}

/** What closes a document's lines (totals, signatures), across its `columns`: in `PrintRows`' close, with the last line */
export function LinesClose({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={columns} className={classes.close}>
        {children}
      </td>
    </tr>
  )
}

/** The signatures on an issued document (none on a draft) */
export const useIssuedSignatures = (subject: Parameters<typeof useSignatures>[0], record: Rec) =>
  useSignatures(subject, record.docstatus === 'draft' ? undefined : String(record.id)).data

/**
 * A4 invoice / tax invoice in the layout Thai customers expect, with the amount in words; signed by the company's
 * authorized signer once issued, and by the customer who received it
 */
export function InvoiceDocument({ record, refs, copy = false }: { record: Rec; refs: RefMap; copy?: boolean }) {
  const { t } = useTranslation()
  const company = useCompany().data
  const signed = useIssuedSignatures('accounting.invoice', record)
  const customer = useQuery({
    queryKey: ['r', 'accounting.customer', 'record', String(record.customerId)],
    queryFn: () => resourceApi('accounting.customer').get(String(record.customerId)),
    enabled: !!record.customerId,
  }).data?.item
  const lines = (record.lines ?? []) as InvoiceLine[]
  const totals = invoiceTotals(lines, Number(record.vatRate ?? 0), Number(record.whtRate ?? 0))
  const branch = (b?: string | null) =>
    !b || b === '00000' ? t('accounting:customer.headOffice') : `${t('accounting:customer.fields.branch')} ${b}`
  const p = (k: string) => t(`accounting:invoice.print.${k}`)
  const f = (k: string) => t(`accounting:invoice.fields.${k}`)

  // A tax invoice only when it charges VAT: freight is exempt, and its invoice is the billing note too
  const docName = t(totals.vat > 0 ? 'accounting:invoice.documentTitle' : 'accounting:invoice.print.title')
  const issued = formatDate(String(record.issueDate), { style: 'long' })

  return (
    <article className={classes.doc}>
      <PrintRunning start={[company?.name, docName]} end={[String(record.number ?? ''), issued, copy ? p('copy') : p('original')]} />
      <header className={classes.head}>
        <div className={classes.from}>
          <div className={classes.company}>{company?.name}</div>
          <div>{company?.address}</div>
          <div>
            {company?.taxId && `${t('accounting:customer.fields.taxId')} ${formatTaxId(company.taxId)} · ${branch(company.branch)}`}
          </div>
          <div>{[formatPhone(company?.phone), company?.email].filter(Boolean).join(' · ')}</div>
        </div>
        <div className={classes.title}>
          <div className={classes.docName}>{docName}</div>
          <div className={classes.copy}>{copy ? p('copy') : p('original')}</div>
        </div>
      </header>

      <section className={classes.parties}>
        <div>
          <div className={classes.caption}>{p('billTo')}</div>
          <div className={classes.strong}>{String(customer?.name ?? refs['accounting.customer']?.[String(record.customerId)] ?? '')}</div>
          <CustomerAddress customer={customer} />
          <CustomerTaxLine customer={customer} />
        </div>
        <dl className={classes.meta}>
          <dt>{f('number')}</dt>
          <dd className={classes.strong}>{String(record.number ?? '')}</dd>
          <dt>{f('issueDate')}</dt>
          <dd>{issued}</dd>
          {record.dueDate ? (
            <>
              <dt>{f('dueDate')}</dt>
              <dd>{formatDate(String(record.dueDate), { style: 'long' })}</dd>
            </>
          ) : null}
        </dl>
      </section>

      <table className={classes.lines}>
        <thead>
          <tr>
            <th style={{ width: '6%' }}>#</th>
            <th>{t('accounting:invoice.line.description')}</th>
            <th data-num style={{ width: '12%' }}>
              {t('accounting:invoice.line.qty')}
            </th>
            <th data-num style={{ width: '17%' }}>
              {t('accounting:invoice.line.unitPrice')}
            </th>
            <th data-num style={{ width: '17%' }}>
              {t('accounting:invoice.line.amount')}
            </th>
          </tr>
        </thead>
        <PrintRows
          rows={lines.map((l, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: printed lines are positional
            <tr key={i}>
              <td>{i + 1}</td>
              <td>{l.description}</td>
              <td data-num>{formatNumber(l.qty)}</td>
              <td data-num>{formatMoney(l.unitPrice)}</td>
              <td data-num>{formatMoney(l.qty * l.unitPrice)}</td>
            </tr>
          ))}
          close={
            <LinesClose columns={5}>
              <section className={classes.summary}>
                <div className={classes.words}>
                  <div className={classes.caption}>{t('accounting:invoice.totals.inWords')}</div>
                  <div className={classes.strong}>({bahtText(totals.total)})</div>
                  {record.notes ? <p className={classes.notes}>{String(record.notes)}</p> : null}
                </div>
                <dl className={classes.totals}>
                  <dt>{t('accounting:invoice.totals.subtotal')}</dt>
                  <dd>{formatMoney(totals.subtotal)}</dd>
                  {totals.vat > 0 && (
                    <>
                      <dt>{t('accounting:invoice.totals.vat', { rate: record.vatRate })}</dt>
                      <dd>{formatMoney(totals.vat)}</dd>
                    </>
                  )}
                  <dt className={classes.grand}>{t('accounting:invoice.totals.total')}</dt>
                  <dd className={classes.grand}>{formatMoney(totals.total)}</dd>
                  {totals.wht > 0 && (
                    <>
                      <dt>{t('accounting:invoice.totals.wht', { rate: record.whtRate })}</dt>
                      <dd>-{formatMoney(totals.wht)}</dd>
                      <dt>{t('accounting:invoice.totals.net')}</dt>
                      <dd>{formatMoney(totals.net)}</dd>
                    </>
                  )}
                </dl>
              </section>

              <footer className={classes.signs}>
                <SignatureMark label={p('receivedBy')} signature={onLine(signed, 'customer')} />
                <SignatureMark label={p('authorized')} signature={onLine(signed, 'authorized')} />
              </footer>
            </LinesClose>
          }
        />
      </table>
    </article>
  )
}
